import { Button, Col, Dropdown, Form, Input, Popover, Row, Table, message } from "antd";
import React, { useEffect, useState } from "react";
import styled from "styled-components";
import COLORS from "../../../common/Colors";
import {
  SearchOutlined,
  DownloadOutlined,
  CloseOutlined,
  FilterOutlined,
  FormOutlined,
  EllipsisOutlined
} from "@ant-design/icons";
import UsersStyle from "./style";
import ViewUsers from "./ViewUser";
import { useNavigate } from "react-router-dom";
import { setEditUserData } from "../../../Redux/systemSlice";
import { useDispatch, useSelector } from "react-redux";
import FetchData from "../../../hooks/FetchData";

// "Users" and "People" are the same underlying entity
// (tymeplushrlmspeople) - this page was a separate, never-finished build
// of the same "manage people who can log in" feature. Wired to the same
// /people/* API that Component/Pages/People/Peoplemenu.js already uses,
// rather than inventing a second backend concept for it.

const StyledTable = styled(Table)`
  padding-top: 20px;
  table {
    width: 100% !important;
  }
  .ant-table-content {
    overflow-x: auto;
    white-space: nowrap;
    border: 1px solid #00000024;
    border-radius: 10px;
  }
  .ant-table-cell {
    padding: 20px;
  }
  .normal-column {
    min-width: 300px;
    white-space: normal;
  }
  .ant-table-thead .ant-table-cell {
    font-weight: 600;
    font-size: 0.9rem;
    color: ${COLORS.SECONDARY_BLACK};
    white-space: nowrap;
  }
  .non-white {
    background: #f9f9f9;
  }
  .ant-table-thead > tr > th::before {
    display: none !important;
  }

  .ant-table-cell-row-hover {
    background: #e3e8fc !important;
  }
`;

const Users = () => {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [tableViewData, setTableViewData] = useState();
  const [userRenderView, setUserRenderView] = useState("table-view");
  const [filterValue, setFilterValue] = useState("");
  const [clickEditTableIcon, setClickEditTableIcon] = useState(false);

  const [showTableIcons, setShowTableIcon] = useState(true);
  const [showTableHover, setShowTableHover] = useState();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const userInfo = useSelector((state) => state.authTracking.userInfo);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await FetchData({
        endPoint: `/people/getuser/${userInfo.clientId}`,
      });

      if (response?.statusCode === 200 && Array.isArray(response?.response)) {
        setUsers(
          response.response.map((item) => ({
            key: item.people_id,
            people_id: item.people_id,
            firstName: item.first_name,
            lastName: item.last_name,
            user: `${item.first_name || ""} ${item.last_name || ""}`.trim() || "—",
            email: item.email || "—",
            userName: item.user_name,
            bio: item.bio,
            timezone: item.time_zone,
            type: item.peopel_type || "—",
            registration: item.created_at
              ? new Date(item.created_at).toLocaleDateString()
              : "—",
            lastLogin: "—",
          }))
        );
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      message.error("Failed to load users");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userInfo?.clientId) return;
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userInfo?.clientId]);

  const hanldeClickEdit = (record) => {
    setClickEditTableIcon(true)
    setUserRenderView("view-table");
    setTableViewData(record);
    dispatch(setEditUserData(record));
  };
  
  const columns1 = [
    {
      title: "User",
      dataIndex: "user",
      key: "user",
      sorter: (a, b) => a.user - b.user,
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
    },
    {
      title: "Registration",
      dataIndex: "registration",
      key: "registration",
    },
    {
      title: "Last Login",
      dataIndex: "lastLogin",
      key: "lastLogin",
    },
    {
      title: "",
      dataIndex: "edit",
      key: "edit",
      width: "10%",
      render: (text, record, index) => {
        return (
          <>
            <div className="table_action_icons">
              {showTableHover === index && showTableIcons ? (
                <>
                  <Popover placement="top" content="Edit">
                    <FormOutlined
                      className="editIcon"
                      onClick={(e) => {
                        e.stopPropagation();
                        hanldeClickEdit(record);
                      }}
                    />
                  </Popover>
                </>
              ) : (
                <EllipsisOutlined className="ellipsisIcon" />
              )}
            </div>
          </>
        );
      },
    },
  ];

  const rowSelection = {
    selectedRowKeys: selectedRowKeys.map((row) => row.key),
    onChange: (selectedRowKeys, newSelectedRows) => {
      setSelectedRowKeys(newSelectedRows);
    },
    onSelect: (record, selected) => {
      if (selected) {
        setSelectedRowKeys([...selectedRowKeys, record]);
      } else {
        setSelectedRowKeys(
          selectedRowKeys.filter((row) => row.key !== record.key)
        );
      }
    },
    onSelectAll: (selected, selectedRowKeys) => {
      if (selected) {
        setSelectedRowKeys(selectedRowKeys);
      } else {
        setSelectedRowKeys([]);
      }
    },
  };

  const onChangeSortedTable = (pagination, filters, sorter, extra) => {
    console.log("params", pagination, filters, sorter, extra);
  };

  const handleClickData = (data) => {
    setUserRenderView("view-table");
    setTableViewData(data);
    dispatch(setEditUserData(data));
  };

  const items = [
    {
      label: "Active",
      key: "active", // Adding a key for better item identification
    },
    {
      label: "InActive",
      key: "inactive",
    },
  ];

  const handleClickFilter = (data) => {
    setFilterValue(data.label);
  };

  return (
    <>
      <UsersStyle>
        <div className="main_container_box">
          {userRenderView === "table-view" ? (
            <>
              <h1 className="user-menu-title">Users</h1>
              <br></br>
              <Row className="users-menu-filter-container">
                <Col className="course_filter_section_with_icon">
                  <Form.Item name="search" className="search-input-field">
                    <Input prefix={<SearchOutlined />} placeholder="Search" />
                  </Form.Item>
                  <Dropdown
                    menu={{
                      items,
                      onClick: ({ key, domEvent }) => {
                        // Find the clicked item based on the key
                        const clickedItem = items.find(
                          (item) => item.key === key
                        );
                        handleClickFilter(clickedItem);
                      },
                    }}
                    title="Status"
                    trigger={["click"]}
                  >
                    <Popover placement="top" content="Filters">
                      <div className="filter_icon_box">
                        <FilterOutlined />
                      </div>
                    </Popover>
                  </Dropdown>

                  {filterValue && (
                    <Button className="active_filter_button">
                      <CloseOutlined onClick={() => setFilterValue("")} />{" "}
                      {filterValue}
                    </Button>
                  )}
                </Col>

                <Col>
                  <Button
                    onClick={() => navigate("/users/adduser")}
                    className="add-user-button"
                  >
                    Add User
                  </Button>
                </Col>
              </Row>
              <StyledTable
                rowSelection={rowSelection}
                dataSource={users}
                loading={loading}
                columns={columns1}
                rowClassName={(record, index) =>
                  index % 2 === 0 ? "" : "non-white"
                }
                rowKey="key"
                id="Table"
                onChange={onChangeSortedTable}
                showSorterTooltip={{
                  target: "sorter-icon",
                }}
                onRow={(record, rowIndex) => ({
                  onClick: () => handleClickData(record),
                  onMouseEnter: () => setShowTableHover(rowIndex),
                  onMouseLeave: () => setShowTableHover(null),
                })}
              />

              <div className="download_csv_con">
                <Popover placement="top" content="Save as CSV">
                  <DownloadOutlined />
                </Popover>
              </div>
            </>
          ) : (
            <ViewUsers
              tableViewData={tableViewData}
              setUserRenderView={setUserRenderView}
              clickEditTableIcon={clickEditTableIcon}
            />
          )}
        </div>
      </UsersStyle>
    </>
  );
};

export default Users;
